import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom, Subscription } from 'rxjs';
import { CampoHolerite, CAMPOS_HOLERITE, CAMPOS_LANCAMENTO, FonteImportacao, FormatoImportacao, Holerite, LancamentoHolerite, PerfilImportacao } from '../../models/holerite.model';
import { EmpresaStateService } from '../../services/empresa-state.service';
import { FormatoRemessaHolerite, HoleritesService, RequisicaoRemessaHolerite, ValidacaoRemessaHolerite } from '../../services/holerites.service';
import { importarHolerites, lerArquivoHolerite, novoHolerite, novoLancamento, numeroHolerite, totaisHolerite, validarHolerite } from './holerite-importacao';
import { planejarArquivosRemessa, PlanoRemessaHolerite } from './holerite-remessa-lotes';
import { paginarHolerite, PaginaHolerite } from './holerite-impressao';
import { configuracaoRemessaDosHolerites } from './holerite-remessa-configuracao';
import { duplicidadesRemessa, DuplicidadeRemessa } from './holerite-remessa-duplicados';

type AbaHolerites = 'importacao' | 'edicao' | 'salvos' | 'impressao' | 'remessa';
interface ConfirmacaoHolerites { titulo: string; texto: string; acao: string; destrutiva: boolean }

@Component({
  selector: 'app-gpl-holerites',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './gpl-holerites.component.html',
  styleUrls: ['../gpl-venda-impressao/gpl-venda-impressao.component.css', './gpl-holerites.component.css'],
})
export class GplHoleritesComponent implements OnInit, OnDestroy {
  readonly camposHolerite = CAMPOS_HOLERITE;
  readonly camposLancamento = CAMPOS_LANCAMENTO;
  readonly destinosImportacao = [
    ...CAMPOS_HOLERITE.map(c => ({ chave: c.chave, label: c.label, grupo: c.grupo })),
    ...CAMPOS_LANCAMENTO.map(c => ({ chave: `lancamento.${c.chave}`, label: `Rubrica: ${c.label}`, grupo: 'Rubricas' }))
  ];
  readonly formatos: FormatoImportacao[] = ['xml', 'json', 'csv'];
  readonly tiposPagamentoRemessa = (CAMPOS_HOLERITE.find(c => c.chave === 'tipoPagamento')?.opcoes || []).filter(opcao => /^[A-U]$/.test(opcao.valor));
  readonly gruposCampos: { nome: string; campos: CampoHolerite[] }[] = [];
  aba: AbaHolerites = 'importacao';
  empresa = '';
  empresaApi = '';
  empresaNome = '';
  documentos: Holerite[] = [];
  perfis: PerfilImportacao[] = [];
  perfil: PerfilImportacao = this.novoPerfil();
  documento: Holerite = novoHolerite();
  origemEdicao: 'novo' | 'salvo' = 'novo';
  carregando = false;
  erroCarregamento = '';
  salvando = false;
  mensagem = '';
  erro = '';
  filtro = '';
  arquivoNome = '';
  conteudoArquivo = '';
  etapaImportacao: 1 | 2 | 3 = 1;
  buscaDestino = '';
  filtroMapeamento: 'todos' | 'mapeados' | 'pendentes' = 'todos';
  revisaoFeita = false;
  errosLeitura: string[] = [];
  fonte: FonteImportacao | null = null;
  candidatos: Holerite[] = [];
  errosImportacao: string[] = [];
  selecionados: Record<string, boolean> = {};
  confirmacao: ConfirmacaoHolerites | null = null;
  private acaoConfirmacao: (() => void | Promise<void>) | null = null;
  private elementoConfirmacao: HTMLElement | null = null;
  private focoAntesConfirmacao: HTMLElement | null = null;
  @ViewChild('modalConfirmacao') set modalConfirmacao(ref: ElementRef<HTMLElement> | undefined) {
    this.elementoConfirmacao = ref?.nativeElement || null;
    this.elementoConfirmacao?.querySelector<HTMLButtonElement>('[data-foco-inicial]')?.focus();
  }
  modoImpressao: 'completo' | 'simplificado' = 'completo';
  localImpressao = '';
  dataReciboImpressao = '';
  formatoRemessa: FormatoRemessaHolerite = 'cnab200';
  readonly tiposRemessa400 = [
    { valor: 'T', label: 'T · Carga de lote', descricao: 'Envia os dados completos dos holerites do lote.' },
    { valor: 'C', label: 'C · Complemento de lote', descricao: 'Envia os dados completos dos holerites para complementar o lote.' },
    { valor: 'S', label: 'S · Substituição de lote', descricao: 'Envia os dados completos dos holerites para substituir o lote.' },
    { valor: 'A', label: 'A · Alteração de holerites', descricao: 'Envia os dados completos dos holerites para alteração.' },
    { valor: 'E', label: 'E · Exclusão individual', descricao: 'Gera os cabeçalhos dos funcionários selecionados, sem os dados completos dos holerites.' },
    { valor: 'D', label: 'D · Dispensa de funcionário', descricao: 'Gera os cabeçalhos dos funcionários selecionados, sem os dados completos dos holerites.' },
    { valor: 'X', label: 'X · Exclusão total de lote', descricao: 'Gera a identificação do lote, sem holerites individuais.' }
  ];
  remessaConfiguracao: Record<string, string> = this.novaConfiguracaoRemessa();
  remessaValidacao: ValidacaoRemessaHolerite | null = null;
  remessaFingerprint = '';
  remessaErro = '';
  remessaCarregando = false;
  remessaBaixando = false;
  tamanhoArquivoRemessa = 500;
  arquivoRemessaIndice = 0;
  arquivosRemessaBaixados: Record<number, string> = {};
  private sequenciasRemessa: Record<number, Record<string, string>> = {};
  private operacaoPlanoRemessa = 'T';
  private configuracaoCompartilhadaRemessa = this.conteudoCompartilhadoRemessa();
  private nomesAutomaticosRemessa: Record<string, string> = {};
  private urlRemessa: string | null = null;
  private versaoEmpresa = 0;
  private versaoDocumentos = 0;
  private versaoArquivo = 0;
  private readonly subscriptions = new Subscription();

  constructor(private readonly empresaState: EmpresaStateService, private readonly api: HoleritesService) {
    const nomes = [...new Set(CAMPOS_HOLERITE.map(c => c.grupo))];
    this.gruposCampos.push(...nomes.map(nome => ({ nome, campos: CAMPOS_HOLERITE.filter(c => c.grupo === nome) })));
  }

  ngOnInit(): void {
    this.subscriptions.add(this.empresaState.empresaMudou.subscribe(cod => this.trocarEmpresa(String(cod || ''))));
    this.trocarEmpresa(String(this.empresaState.selecionada || ''));
  }

  ngOnDestroy(): void { this.fecharConfirmacao(false); this.versaoEmpresa++; this.subscriptions.unsubscribe(); this.revogarUrlRemessa(); }

  private confirmarAcao(confirmacao: ConfirmacaoHolerites, acao: () => void | Promise<void>): void {
    if (this.confirmacao) return;
    this.focoAntesConfirmacao = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.confirmacao = confirmacao;
    this.acaoConfirmacao = acao;
  }
  fecharConfirmacao(confirmada = false): void {
    const acao = this.acaoConfirmacao;
    const foco = this.focoAntesConfirmacao;
    this.confirmacao = null; this.acaoConfirmacao = null; this.focoAntesConfirmacao = null;
    if (foco) window.setTimeout(() => { if (!this.confirmacao && foco.isConnected) foco.focus(); });
    if (confirmada) void acao?.();
  }
  @HostListener('document:keydown', ['$event'])
  tecladoConfirmacao(event: KeyboardEvent): void {
    if (!this.confirmacao) return;
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.fecharConfirmacao(false); return; }
    if (event.key !== 'Tab' || !this.elementoConfirmacao) return;
    const botoes = Array.from(this.elementoConfirmacao.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'));
    const primeiro = botoes[0], ultimo = botoes[botoes.length - 1];
    if (!primeiro || !ultimo) return;
    if (!this.elementoConfirmacao.contains(document.activeElement)) { event.preventDefault(); (event.shiftKey ? ultimo : primeiro).focus(); }
    else if (event.shiftKey && document.activeElement === primeiro) { event.preventDefault(); ultimo.focus(); }
    else if (!event.shiftKey && document.activeElement === ultimo) { event.preventDefault(); primeiro.focus(); }
  }

  private async trocarEmpresa(cod: string): Promise<void> {
    this.fecharConfirmacao(false);
    const token = ++this.versaoEmpresa;
    this.versaoDocumentos++;
    this.empresa = cod;
    const opcaoEmpresa = this.empresaState.opcoes.find(o => o.codEmp === cod);
    this.empresaApi = opcaoEmpresa?.virtual && opcaoEmpresa.codEmpReal ? opcaoEmpresa.codEmpReal : cod;
    this.empresaNome = opcaoEmpresa?.nomeFant || '';
    this.documentos = []; this.perfis = []; this.perfil = this.novoPerfil(); this.documentosImpressao = [];
    this.documento = novoHolerite(); this.origemEdicao = 'novo'; this.aba = 'importacao'; this.carregando = false; this.salvando = false;
    this.localImpressao = ''; this.dataReciboImpressao = '';
    this.fonte = null; this.candidatos = []; this.errosImportacao = []; this.errosLeitura = []; this.selecionados = {};
    this.etapaImportacao = 1; this.revisaoFeita = false; this.buscaDestino = ''; this.filtroMapeamento = 'todos'; this.erroCarregamento = '';
    this.versaoArquivo++;
    this.conteudoArquivo = ''; this.arquivoNome = '';
    this.mensagem = ''; this.erro = '';
    this.nomesAutomaticosRemessa = {};
    this.remessaConfiguracao = this.novaConfiguracaoRemessa(); this.tamanhoArquivoRemessa = 500; this.reiniciarArquivosRemessa(false); this.invalidarRemessa(); this.remessaCarregando = false; this.remessaBaixando = false;
    this.documento.dados['empresaNome'] = this.empresaNome;
    if (!cod) return;
    await this.carregarDadosEmpresa(token);
  }

  private async carregarDadosEmpresa(token: number): Promise<void> {
    if (!this.empresaApi) return;
    this.carregando = true;
    try {
      const [perfis, docs] = await Promise.all([firstValueFrom(this.api.listarPerfis(this.empresaApi)), firstValueFrom(this.api.listarDocumentos(this.empresaApi))]);
      if (token !== this.versaoEmpresa) return;
      const listaPerfis = Array.isArray(perfis) ? perfis : [];
      const listaDocs = Array.isArray(docs) ? docs : [];
      if (token !== this.versaoEmpresa) return;
      this.perfis = listaPerfis;
      this.documentos = listaDocs;
      this.versaoDocumentos++;
      this.reiniciarArquivosRemessa();
      this.sincronizarNomesRemessa();
      this.invalidarRemessa();
      this.erroCarregamento = '';
    } catch (e) { if (token === this.versaoEmpresa) this.erroCarregamento = this.mensagemErro(e, 'Não foi possível conectar para carregar perfis e documentos.'); }
    finally { if (token === this.versaoEmpresa) this.carregando = false; }
  }

  tentarNovamente(): void { void this.carregarDadosEmpresa(this.versaoEmpresa); }

  abrirNovo(): void {
    this.documento = novoHolerite(); this.documento.dados['empresaNome'] = this.empresaNome;
    this.origemEdicao = 'novo'; this.aba = 'edicao'; this.mensagem = ''; this.erro = '';
  }

  editar(h: Holerite): void {
    this.documento = { ...h, dados: { ...h.dados }, lancamentos: h.lancamentos.map(l => ({ ...l })) };
    this.origemEdicao = 'salvo'; this.aba = 'edicao'; this.mensagem = ''; this.erro = '';
  }

  campo(chave: string): string { return this.documento.dados[chave] || ''; }
  competenciaFormatada(valor: string): string { return /^(\d{4})-(\d{2})$/.test(valor || '') ? valor.slice(5) + '/' + valor.slice(0, 4) : valor; }
  dataFormatada(valor: string): string { return /^(\d{4})-(\d{2})-(\d{2})$/.test(valor || '') ? valor.slice(8) + '/' + valor.slice(5, 7) + '/' + valor.slice(0, 4) : valor; }
  tipoPagamentoLabel(valor: string): string { return CAMPOS_HOLERITE.find(c => c.chave === 'tipoPagamento')?.opcoes?.find(o => o.valor === valor)?.label || valor; }
  definirCampo(chave: string, valor: string): void { this.documento.dados[chave] = valor; }
  trackByChave(_: number, item: { chave: string }): string { return item.chave; }
  trackByIndex(index: number): number { return index; }
  trackById(_: number, item: Holerite): string { return item.id || item.dados['numeroDocumento'] || item.dados['funcionarioMatricula'] || ''; }
  rubricasImportacao(): number { return this.candidatos.reduce((total, h) => total + h.lancamentos.length, 0); }
  totais(h = this.documento) { try { return totaisHolerite(h); } catch { return { creditos: Number.NaN, debitos: Number.NaN, liquido: Number.NaN }; } }
  moeda(valor: number): string { return Number.isFinite(valor) ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor) : 'Preencha os valores'; }
  moedaInformada(h: Holerite, chave: string): string { const valor = h.dados[chave] || ''; if (!valor.trim()) return 'Não informado'; try { return this.moeda(numeroHolerite(valor)); } catch { return valor; } }
  totalImpresso(h: Holerite, qual: 'creditos' | 'debitos' | 'liquido'): string {
    if (h.lancamentos.length) return this.moeda(this.totais(h)[qual]);
    const campo = qual === 'creditos' ? 'totalCreditoInformado' : qual === 'debitos' ? 'totalDebitoInformado' : 'totalLiquidoInformado';
    return this.moedaInformada(h, campo);
  }
  valorRubrica(valor: string): string { try { return this.moeda(numeroHolerite(valor)); } catch { return valor || '—'; } }
  paginasLancamentos(h: Holerite): PaginaHolerite[] { return paginarHolerite(h, this.modoImpressao); }
  perfilDestino(chave: string): string { return this.perfil.mapeamento[chave] || ''; }

  novaRubrica(): void {
    const item = novoLancamento();
    item.matricula = this.documento.dados['funcionarioMatricula'] || '';
    item.sequencia = String(this.documento.lancamentos.reduce((max, lancamento) => Math.max(max, Number(lancamento.sequencia) || 0), 0) + 1);
    if (!item.quantidade) item.quantidade = '0';
    this.documento.lancamentos.push(item);
  }
  removerRubrica(index: number): void { this.documento.lancamentos.splice(index, 1); }

  async salvarDocumento(): Promise<void> {
    if (!this.empresa) { this.erro = 'Selecione uma empresa para salvar.'; return; }
    const erros = validarHolerite(this.documento);
    if (erros.length) { this.erro = erros.join(' '); this.mensagem = ''; return; }
    this.salvando = true; this.erro = ''; this.mensagem = '';
    const token = this.versaoEmpresa;
    try {
      const saved = await firstValueFrom(this.api.salvarDocumento(this.empresaApi, this.documento));
      if (token !== this.versaoEmpresa) return;
      const h = (saved as Holerite) || this.documento;
      this.documento = { ...h, dados: { ...h.dados }, lancamentos: h.lancamentos.map(l => ({ ...l })) };
      this.origemEdicao = 'salvo';
      await this.atualizarDocumentos(token);
      this.mensagem = 'Demonstrativo salvo.';
    } catch (e) { if (token === this.versaoEmpresa) this.erro = this.mensagemErro(e, 'Não foi possível salvar o demonstrativo.'); }
    finally { if (token === this.versaoEmpresa) this.salvando = false; }
  }

  excluirDocumento(h: Holerite): void {
    if (!h.id || this.carregando || this.salvando || this.erroCarregamento) return;
    const token = this.versaoEmpresa;
    const id = h.id;
    this.confirmarAcao({titulo: 'Excluir demonstrativo', texto: `Excluir o demonstrativo de ${h.dados['funcionarioNome'] || 'funcionário'}?`, acao: 'Excluir demonstrativo', destrutiva: true}, async () => {
      if (token !== this.versaoEmpresa || this.carregando || this.salvando || this.erroCarregamento) return;
      this.salvando = true;
      try { await firstValueFrom(this.api.excluirDocumento(this.empresaApi, id)); if (token !== this.versaoEmpresa) return; await this.atualizarDocumentos(token); this.mensagem = 'Demonstrativo excluído.'; }
      catch (e) { if (token === this.versaoEmpresa) this.erro = this.mensagemErro(e, 'Não foi possível excluir o demonstrativo.'); }
      finally { if (token === this.versaoEmpresa) this.salvando = false; }
    });
  }

  async salvarPerfil(): Promise<void> {
    if (this.salvando || this.carregando) return;
    if (!this.empresa || !this.perfil.nome.trim()) { this.erro = 'Informe o nome do perfil e selecione uma empresa.'; return; }
    if (this.erroCarregamento) { this.erro = 'Não foi possível salvar o perfil enquanto a conexão de perfis estiver indisponível.'; return; }
    if (!this.fonte || !this.mapeamentoValido()) { this.erro = 'Carregue o arquivo e confira os campos de origem antes de salvar o perfil.'; return; }
    const token = this.versaoEmpresa;
    this.salvando = true;
    try {
      const result = await firstValueFrom(this.api.salvarPerfil(this.empresaApi, this.perfil));
      if (token !== this.versaoEmpresa) return;
      this.perfil = { ...(result as PerfilImportacao || this.perfil), mapeamento: { ...(result as PerfilImportacao)?.mapeamento || this.perfil.mapeamento } };
      const lista = await firstValueFrom(this.api.listarPerfis(this.empresaApi));
      if (token !== this.versaoEmpresa) return;
      this.perfis = lista;
      this.mensagem = 'Perfil de importação salvo.'; this.erro = '';
    } catch (e) { if (token === this.versaoEmpresa) this.erro = this.mensagemErro(e, 'Não foi possível salvar o perfil.'); }
    finally { if (token === this.versaoEmpresa) this.salvando = false; }
  }

  selecionarPerfil(id: string): void {
    const item = this.perfis.find(p => p.id === id);
    this.perfil = item ? { ...item, mapeamento: { ...item.mapeamento } } : this.novoPerfil();
    this.limparPrevia();
    this.errosLeitura = [];
    if (this.conteudoArquivo) this.carregarFonte();
  }

  removerArquivo(): void { this.versaoArquivo++; this.conteudoArquivo = ''; this.arquivoNome = ''; this.fonte = null; this.errosLeitura = []; this.etapaImportacao = 1; this.limparPrevia(); }

  excluirPerfil(): void {
    if (this.salvando || this.carregando || this.erroCarregamento || !this.perfil.id) return;
    const id = this.perfil.id;
    const nome = this.perfis.find(p => p.id === id)?.nome || this.perfil.nome;
    const token = this.versaoEmpresa;
    this.confirmarAcao({titulo: 'Excluir perfil', texto: `Excluir o perfil salvo "${nome}" e seu de-para?`, acao: 'Excluir perfil', destrutiva: true}, async () => {
      if (token !== this.versaoEmpresa || this.salvando || this.carregando || this.erroCarregamento) return;
      this.salvando = true;
      try {
        await firstValueFrom(this.api.excluirPerfil(this.empresaApi, id));
        if (token !== this.versaoEmpresa) return;
        this.perfis = this.perfis.filter(p => p.id !== id);
        if (this.perfil.id === id) {
          this.perfil = { ...this.perfil, id: undefined, nome: '', mapeamento: { ...this.perfil.mapeamento } };
          this.limparPrevia();
          if (this.etapaImportacao === 3) this.etapaImportacao = 2;
        }
        this.mensagem = 'Perfil excluído.'; this.erro = '';
      }
      catch (e) { if (token === this.versaoEmpresa) this.erro = this.mensagemErro(e, 'Não foi possível excluir o perfil.'); }
      finally { if (token === this.versaoEmpresa) this.salvando = false; }
    });
  }

  aoSelecionarArquivo(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const tokenArquivo = ++this.versaoArquivo;
    this.limparPrevia(); this.fonte = null; this.errosLeitura = []; this.etapaImportacao = 1;
    this.conteudoArquivo = ''; this.arquivoNome = file?.name || '';
    if (!file) return;
    input.value = '';
    if (file.size > 5 * 1024 * 1024) { this.errosLeitura = ['O arquivo excede o limite de 5 MB.']; return; }
    this.errosImportacao = [];
    if (!this.perfil.id) {
      const ext = file.name.split('.').pop()?.toLowerCase();
      if ((ext === 'xml' || ext === 'json' || ext === 'csv') && ext !== this.perfil.formato) {
        this.perfil.formato = ext;
        this.perfil.decimal = ext === 'csv' ? ',' : '.';
      }
      if (!this.perfil.nome.trim()) this.perfil.nome = file.name.replace(/\.[^.]+$/, '').slice(0, 100);
    }
    const token = this.versaoEmpresa;
    const reader = new FileReader();
    reader.onload = () => { if (token !== this.versaoEmpresa || tokenArquivo !== this.versaoArquivo) return; this.conteudoArquivo = String(reader.result || ''); this.carregarFonte(); };
    reader.onerror = () => { if (token === this.versaoEmpresa && tokenArquivo === this.versaoArquivo) this.errosLeitura = ['Não foi possível ler o arquivo selecionado.']; };
    reader.readAsText(file);
  }

  private carregarFonte(): void {
    this.fonte = null; this.errosLeitura = []; this.limparPrevia();
    if (!this.conteudoArquivo) { this.errosLeitura = ['Selecione um arquivo para continuar.']; return; }
    try {
      this.fonte = lerArquivoHolerite(this.conteudoArquivo, this.perfil.formato, this.perfil.caminhoRegistros, this.perfil.separador);
      if (!this.fonte.registros.length) { this.fonte = null; this.errosLeitura = ['Nenhum registro foi encontrado no arquivo.']; return; }
      if (!this.perfil.id) {
        for (const campo of CAMPOS_HOLERITE) if (!this.perfil.mapeamento[campo.chave] && this.fonte.campos.includes(campo.chave)) this.perfil.mapeamento[campo.chave] = campo.chave;
        for (const campo of CAMPOS_LANCAMENTO) if (!this.perfil.mapeamento[`lancamento.${campo.chave}`]) {
          const origem = [`lancamento.${campo.chave}`, `lancamentos.${campo.chave}`, `lancamentos.lancamento.${campo.chave}`].find(c => this.fonte?.campos.includes(c));
          if (origem) this.perfil.mapeamento[`lancamento.${campo.chave}`] = origem;
        }
      }
    } catch (e) { this.fonte = null; this.errosLeitura = [this.mensagemErro(e, 'Arquivo inválido.')]; }
  }

  configuracaoAlterada(): void { this.limparPrevia(); this.errosLeitura = []; this.etapaImportacao = 1; if (this.conteudoArquivo) this.carregarFonte(); }
  formatoAlterado(): void { this.perfil.decimal = this.perfil.formato === 'csv' ? ',' : '.'; this.configuracaoAlterada(); }
  continuarParaMapa(): void { if (!this.fonte || this.errosLeitura.length) return; this.etapaImportacao = 2; this.erro = ''; }
  voltarEtapa(n: 1 | 2): void { this.etapaImportacao = n; this.erro = ''; }
  destino(chave: string, valor: string): void { this.perfil.mapeamento[chave] = valor; this.limparPrevia(); if (this.etapaImportacao === 3) this.etapaImportacao = 2; }
  fontesMapeamento(chave: string): string[] {
    const campos = this.fonte?.campos || [];
    const mapeado = this.perfilDestino(chave);
    const mesmo = [chave, chave.replace(/^lancamento\./, 'lancamentos.'), chave.replace(/^lancamento\./, 'lancamentos.lancamento.')];
    const preferidos = [...mesmo.filter(c => campos.includes(c)), ...(mapeado && campos.includes(mapeado) ? [mapeado] : [])];
    return [...new Set([...preferidos, ...campos.filter(c => !preferidos.includes(c))])];
  }
  get destinosMapeados(): { chave: string; label: string; grupo: string }[] {
    const q = this.buscaDestino.trim().toLocaleLowerCase('pt-BR');
    return this.destinosImportacao.filter(item => {
      const origem = this.perfilDestino(item.chave);
      if (this.filtroMapeamento === 'mapeados' && !origem) return false;
      if (this.filtroMapeamento === 'pendentes' && origem) return false;
      if (!q) return true;
      return `${item.label} ${item.grupo} ${origem}`.toLocaleLowerCase('pt-BR').includes(q);
    });
  }
  get gruposMapeamento(): { chave: string; nome: string; campos: { chave: string; label: string; grupo: string }[] }[] {
    const grupos: { chave: string; nome: string; campos: { chave: string; label: string; grupo: string }[] }[] = [];
    // Group consecutive destinations without moving any field from its catalog/XML order.
    for (const campo of this.destinosMapeados) {
      let grupo = grupos[grupos.length - 1];
      if (!grupo || grupo.nome !== campo.grupo) { grupo = { chave: campo.chave, nome: campo.grupo, campos: [] }; grupos.push(grupo); }
      grupo.campos.push(campo);
    }
    return grupos;
  }
  trackByGrupo(_: number, grupo: { chave: string }): string { return grupo.chave; }
  mapeamentoValido(): boolean {
    const entries = Object.entries(this.perfil.mapeamento || {}).filter(([, source]) => !!source);
    return !!this.fonte && entries.length > 0 && entries.every(([, source]) => this.fonte!.campos.includes(source));
  }
  salvarPerfilHabilitado(): boolean { return !!this.perfil.nome.trim() && this.mapeamentoValido() && !this.erroCarregamento && !this.salvando && !this.carregando; }
  validarImportacao(): void {
    this.candidatos = []; this.errosImportacao = []; this.revisaoFeita = false;
    if (!this.fonte || !this.mapeamentoValido()) { this.errosImportacao = ['Volte ao passo 2 e corrija os campos de origem inexistentes.']; this.etapaImportacao = 3; this.revisaoFeita = true; return; }
    const resultado = importarHolerites(this.fonte, this.perfil);
    const validacao = resultado.holerites.flatMap((h, i) => validarHolerite(h).map(msg => `Demonstrativo ${i + 1}: ${msg}`));
    this.candidatos = resultado.holerites;
    this.errosImportacao = [...resultado.erros, ...validacao];
    if (!this.candidatos.length && !this.errosImportacao.length) this.errosImportacao = ['Nenhum demonstrativo foi formado pelo de-para atual.'];
    this.revisaoFeita = true; this.etapaImportacao = 3; this.erro = '';
  }

  async importar(): Promise<void> {
    if (!this.empresa || !this.fonte) { this.erro = 'Selecione a empresa e visualize um arquivo válido antes de importar.'; return; }
    if (this.erroCarregamento) { this.erro = 'Os documentos estão indisponíveis no servidor. O arquivo foi validado localmente, mas não foi importado.'; return; }
    if (!this.revisaoFeita || this.etapaImportacao !== 3) { this.erro = 'Valide o de-para e revise os registros antes de importar.'; return; }
    const resultado = importarHolerites(this.fonte, this.perfil);
    const validation = resultado.holerites.flatMap((h, i) => validarHolerite(h).map(msg => `Registro ${i + 1}: ${msg}`));
    const errors = [...resultado.erros, ...validation];
    if (errors.length || !resultado.holerites.length) {
      this.errosImportacao = errors.length ? errors : ['Nem todos os registros puderam ser convertidos. Nenhum documento foi importado.']; return;
    }
    this.salvando = true; const token = this.versaoEmpresa; let gravados = 0;
    try {
      for (const h of resultado.holerites) { await firstValueFrom(this.api.salvarDocumento(this.empresaApi, h)); if (token !== this.versaoEmpresa) return; gravados++; }
      await this.atualizarDocumentos(token);
      if (token !== this.versaoEmpresa) return;
      if (!this.remessaConfiguracao['empresaConvenio']) {
        const configuracao = configuracaoRemessaDosHolerites(resultado.holerites);
        Object.assign(this.remessaConfiguracao, configuracao);
        this.registrarNomesAutomaticos(configuracao);
        this.remessaAlterada();
      }
      this.candidatos = []; this.conteudoArquivo = ''; this.arquivoNome = ''; this.fonte = null; this.etapaImportacao = 1; this.revisaoFeita = false; this.mensagem = `${gravados} demonstrativo(s) importado(s).`; this.errosImportacao = [];
    } catch (e) { if (token === this.versaoEmpresa) { try { await this.atualizarDocumentos(token); } catch { /* mantém visível a falha original da importação */ } this.erro = `Falha ao salvar a importação após ${gravados} documento(s). ${this.mensagemErro(e, 'Revise a lista de salvos antes de tentar novamente.')}`; } }
    finally { if (token === this.versaoEmpresa) this.salvando = false; }
  }

  marcarVisiveis(checked: boolean): void { for (const h of this.visiveis()) if (h.id) this.selecionados[h.id] = checked; this.selecaoRemessaAlterada(); }
  marcarTodos(checked: boolean): void { for (const h of this.documentos) if (h.id) this.selecionados[h.id] = checked; this.selecaoRemessaAlterada(); }
  selecionadosParaImprimir(): Holerite[] { return this.documentos.filter(h => !!h.id && this.selecionados[h.id]); }
  podeImprimir(h: Holerite): boolean { return !/\b[SD]\b/i.test(h.dados['bloqueio'] || ''); }
  bloqueioExplicado(h: Holerite): string { return this.podeImprimir(h) ? '' : `Impressão bloqueada: bloqueio S/D informado. Confira os dados bancários do funcionário ${h.dados['funcionarioNome'] || ''}.`; }

  prepararImpressao(h?: Holerite): void {
    this.documentosImpressao = [];
    const lista = h ? [h] : this.selecionadosParaImprimir();
    if (!lista.length) { this.erro = 'Selecione ao menos um demonstrativo para imprimir.'; return; }
    const invalidos = lista.flatMap((item, i) => validarHolerite(item).map(msg => `Documento ${i + 1}: ${msg}`));
    if (invalidos.length) { this.erro = `Impressão bloqueada. Corrija os dados: ${invalidos.join(' ')}`; return; }
    const bloqueados = lista.filter(x => !this.podeImprimir(x));
    if (bloqueados.length) { this.erro = `Impressão bloqueada para ${bloqueados.length} demonstrativo(s) com bloqueio S/D. Confira o campo Bloqueio nos dados importados.`; return; }
    this.documentosImpressao = lista; this.aba = 'impressao'; this.erro = ''; this.mensagem = 'Prévia preparada. Revise o modelo e depois escolha Imprimir / Salvar PDF.';
  }
  abrirAbaImpressao(): void {
    if (this.aba === 'edicao') {
      const invalid = validarHolerite(this.documento);
      if (invalid.length) { this.erro = invalid.join(' '); return; }
      this.prepararImpressao(this.documento); return;
    }
    if (this.aba === 'salvos' && this.selecionadosParaImprimir().length) { this.prepararImpressao(); return; }
    this.aba = 'impressao';
  }
  imprimirPrevia(): void { if (this.documentosImpressao.length) window.print(); else this.erro = 'Prepare ao menos um demonstrativo válido antes de imprimir.'; }
  documentosImpressao: Holerite[] = [];
  voltarDaImpressao(): void { this.aba = 'salvos'; }

  trocarFormatoRemessa(formato: FormatoRemessaHolerite): void { this.reiniciarArquivosRemessa(); this.formatoRemessa = formato; this.remessaAlterada(); }
  tipoRemessa400ComLoteVersao(): boolean { return this.formatoRemessa === 'cnab400' && ['T', 'D'].includes(this.remessaConfiguracao['tipoArquivo']); }
  descricaoTipoRemessa400(): string { return this.tiposRemessa400.find(t => t.valor === this.remessaConfiguracao['tipoArquivo'])?.descricao || ''; }
  remessaAlterada(): void {
    if (this.operacaoPlanoRemessa !== this.remessaConfiguracao['tipoArquivo']) this.reiniciarArquivosRemessa();
    const compartilhada = this.conteudoCompartilhadoRemessa();
    if (compartilhada !== this.configuracaoCompartilhadaRemessa) this.arquivosRemessaBaixados = {};
    else delete this.arquivosRemessaBaixados[this.arquivoRemessaIndice];
    this.configuracaoCompartilhadaRemessa = compartilhada;
    this.invalidarRemessa(); this.remessaErro = '';
  }
  selecaoRemessaAlterada(): void { this.reiniciarArquivosRemessa(); this.sincronizarNomesRemessa(); this.invalidarRemessa(); this.remessaErro = ''; }
  private documentosDoArquivoRemessa(): Holerite[] {
    return this.planoRemessa.arquivos[this.arquivoRemessaIndice]?.documentos || this.selecionadosParaImprimir();
  }
  private registrarNomesAutomaticos(configuracao: Record<string, string>): void {
    for (const chave of ['empresaNome', 'empresaNomeReduzido']) {
      if (configuracao[chave]) this.nomesAutomaticosRemessa[chave] = configuracao[chave];
    }
  }
  private sincronizarNomesRemessa(): void {
    const documentos = this.documentosDoArquivoRemessa();
    if (!documentos.length) return;
    const configuracao = configuracaoRemessaDosHolerites(documentos);
    const cnpj = (this.remessaConfiguracao['empresaCnpj'] || '').replace(/\D/g, '');
    if (cnpj && cnpj !== (configuracao['empresaCnpj'] || '').replace(/\D/g, '')) return;
    for (const chave of ['empresaNome', 'empresaNomeReduzido']) {
      const atual = this.remessaConfiguracao[chave] || '';
      // Mantém edições manuais; recalcula campos vazios ou preenchidos pelo sistema.
      if (!atual.trim() || atual === this.nomesAutomaticosRemessa[chave]) {
        this.remessaConfiguracao[chave] = configuracao[chave] || '';
        this.nomesAutomaticosRemessa[chave] = this.remessaConfiguracao[chave];
      }
    }
  }
  get avisoNomesRemessa(): string {
    const documentos = this.documentosDoArquivoRemessa();
    const campos = [{chave: 'empresaNome', nome: 'Nome da empresa'}, {chave: 'empresaNomeReduzido', nome: 'Nome reduzido'}];
    const divergentes = campos.filter(c => new Set(documentos.map(h => (h.dados[c.chave] || '').trim())).size > 1).map(c => c.nome);
    return divergentes.length ? `${divergentes.join(' e ')}: valores diferentes entre os demonstrativos do arquivo atual. Resolva as versões anteriores na etapa 1 ou confira os campos manualmente. Após ajustar a seleção, os nomes são preenchidos automaticamente quando houver um valor comum.` : '';
  }
  get duplicidadesSelecionadas(): DuplicidadeRemessa[] {
    return duplicidadesRemessa(this.selecionadosParaImprimir(), this.formatoRemessa, this.remessaConfiguracao);
  }
  get versoesAnterioresSelecionadas(): number {
    return this.duplicidadesSelecionadas.reduce((total, grupo) => total + (grupo.maisRecente ? grupo.documentos.length - 1 : 0), 0);
  }
  desmarcarVersoesAnteriores(): void {
    if (this.carregando || this.salvando || this.remessaCarregando || this.remessaBaixando) return;
    const grupos = this.duplicidadesSelecionadas.filter(grupo => grupo.maisRecente);
    const quantidade = grupos.reduce((total, grupo) => total + grupo.documentos.length - 1, 0);
    if (!quantidade) return;
    const fingerprint = this.fingerprintRemessa();
    this.confirmarAcao({titulo: 'Desmarcar versões anteriores', texto: `Desmarcar ${quantidade} versão(ões) anterior(es) e manter a importação mais recente de cada holerite? Confira as versões na lista antes de continuar. Todos os documentos continuarão salvos; valores e rubricas não serão mesclados.`, acao: 'Desmarcar versões', destrutiva: false}, () => {
      if (fingerprint !== this.fingerprintRemessa()) { this.remessaErro = 'A seleção ou configuração mudou. Confira as versões e tente novamente.'; return; }
      for (const grupo of grupos) for (const h of grupo.documentos) {
        if (h.id && h.id !== grupo.maisRecente!.id) this.selecionados[h.id] = false;
      }
      this.selecaoRemessaAlterada();
      this.mensagem = `${quantidade} versão(ões) anterior(es) desmarcada(s). ${this.selecionadosParaImprimir().length} demonstrativo(s) selecionado(s). Confira a seleção e valide novamente. Todos os documentos continuam salvos.`;
    });
  }
  get planoRemessa(): PlanoRemessaHolerite {
    return planejarArquivosRemessa(this.selecionadosParaImprimir(), this.formatoRemessa, this.remessaConfiguracao['tipoArquivo'], Number(this.tamanhoArquivoRemessa));
  }
  tamanhoArquivoRemessaAlterado(valor: number): void { this.tamanhoArquivoRemessa = valor; this.selecaoRemessaAlterada(); }
  escolherArquivoRemessa(indice: number): void {
    if (!Number.isInteger(indice) || indice < 0 || indice >= this.planoRemessa.arquivos.length || indice === this.arquivoRemessaIndice || this.remessaCarregando || this.remessaBaixando) return;
    this.sequenciasRemessa[this.arquivoRemessaIndice] = this.sequenciaAtualRemessa();
    const sequencia = this.sequenciasRemessa[indice] || {
      numeroRemessa: '', versaoArquivo: '',
      numeroLote: this.tipoRemessa400ComLoteVersao() ? '' : this.remessaConfiguracao['numeroLote']
    };
    Object.assign(this.remessaConfiguracao, sequencia);
    this.arquivoRemessaIndice = indice; this.invalidarRemessa(); this.remessaErro = '';
  }
  arquivoRemessaBaixado(indice: number): boolean {
    return indice === this.arquivoRemessaIndice
      ? this.arquivosRemessaBaixados[indice] === this.fingerprintRemessa()
      : !!this.arquivosRemessaBaixados[indice];
  }
  private sequenciaAtualRemessa(): Record<string, string> {
    return Object.fromEntries(['numeroRemessa', 'versaoArquivo', 'numeroLote'].map(k => [k, this.remessaConfiguracao[k]]));
  }
  private conteudoCompartilhadoRemessa(): string {
    return JSON.stringify(Object.entries(this.remessaConfiguracao).filter(([k]) => !['numeroRemessa', 'versaoArquivo', 'numeroLote'].includes(k)));
  }
  private reiniciarArquivosRemessa(restaurar = true): void {
    if (restaurar && this.arquivoRemessaIndice > 0 && this.sequenciasRemessa[0]) Object.assign(this.remessaConfiguracao, this.sequenciasRemessa[0]);
    this.arquivoRemessaIndice = 0; this.sequenciasRemessa = {}; this.arquivosRemessaBaixados = {};
    this.operacaoPlanoRemessa = this.remessaConfiguracao['tipoArquivo'];
    this.configuracaoCompartilhadaRemessa = this.conteudoCompartilhadoRemessa();
  }
  private erroSequenciaRemessa(): string {
    const chave = this.formatoRemessa === 'cnab200' ? 'numeroRemessa' : 'versaoArquivo';
    const atual = Number(this.remessaConfiguracao[chave]);
    if (!Number.isSafeInteger(atual) || atual <= 0) return '';
    for (const [indice, cfg] of Object.entries(this.sequenciasRemessa)) {
      const n = Number(indice); const outro = Number(cfg[chave]);
      if (n === this.arquivoRemessaIndice || !Number.isSafeInteger(outro) || outro <= 0) continue;
      if ((n < this.arquivoRemessaIndice && outro >= atual) || (n > this.arquivoRemessaIndice && outro <= atual)) {
        return 'A sequência deve ser crescente e diferente em cada arquivo. Confira os números acordados com o Santander.';
      }
    }
    return '';
  }
  preencherRemessaComDemonstrativo(): void {
    const documentos = this.documentosDoArquivoRemessa();
    const fonte = documentos.length ? documentos : this.documentos.slice(0, 1);
    if (!fonte.length) { this.remessaErro = 'Salve ao menos um demonstrativo antes de preencher os dados da empresa.'; return; }
    const configuracao = configuracaoRemessaDosHolerites(fonte);
    // A sequência de origem não pode ser reutilizada automaticamente nas partes seguintes.
    if (this.arquivoRemessaIndice > 0) for (const chave of ['numeroRemessa', 'versaoArquivo', 'numeroLote']) delete configuracao[chave];
    Object.assign(this.remessaConfiguracao, configuracao);
    this.registrarNomesAutomaticos(configuracao);
    this.remessaAlterada();
  }
  idsRemessa(): string[] {
    if (this.formatoRemessa === 'cnab400' && this.remessaConfiguracao['tipoArquivo'] === 'X') return [];
    return (this.planoRemessa.arquivos[this.arquivoRemessaIndice]?.documentos || []).map(h => h.id!).filter(Boolean).sort();
  }
  remessaPodeValidar(): boolean {
    return !!this.empresaApi && !this.erroCarregamento && !this.planoRemessa.erro && !this.duplicidadesSelecionadas.length && !this.carregando && !this.salvando && !this.remessaCarregando && !this.remessaBaixando &&
      (this.idsRemessa().length > 0 || (this.formatoRemessa === 'cnab400' && this.remessaConfiguracao['tipoArquivo'] === 'X'));
  }
  remessaPodeBaixar(): boolean {
    return this.remessaPodeValidar() && !!this.remessaValidacao?.valido && !this.remessaValidacao.erros?.length &&
      this.remessaFingerprint === this.fingerprintRemessa() && !!this.remessaValidacao.assinatura;
  }
  async validarRemessa(): Promise<void> {
    if (this.duplicidadesSelecionadas.length) { this.invalidarRemessa(); this.remessaErro = 'Há holerites repetidos na seleção. Revise as versões na etapa 1 e mantenha apenas uma de cada holerite antes de validar.'; return; }
    if (!this.remessaPodeValidar()) { this.remessaErro = this.erroCarregamento ? 'A conexão está indisponível. Tente novamente antes de validar a remessa.' : 'Selecione demonstrativos salvos para validar.'; return; }
    const erroSequencia = this.erroSequenciaRemessa();
    if (erroSequencia) { this.invalidarRemessa(); this.remessaErro = erroSequencia; return; }
    const token = this.versaoEmpresa;
    const fingerprint = this.fingerprintRemessa();
    const requisicao = this.requisicaoRemessa();
    this.remessaValidacao = null; this.remessaFingerprint = ''; this.remessaErro = ''; this.remessaCarregando = true;
    try {
      const resultado = await firstValueFrom(this.api.validarRemessa(this.empresaApi, requisicao));
      if (token !== this.versaoEmpresa) return;
      if (this.carregando || this.salvando || this.erroCarregamento) { this.remessaErro = 'Os dados ainda estão sendo atualizados. Aguarde e valide novamente.'; return; }
      if (fingerprint !== this.fingerprintRemessa()) { this.invalidarRemessa(); return; }
      this.remessaValidacao = resultado;
      this.remessaFingerprint = fingerprint;
    } catch (e) {
      const mensagem = await this.mensagemErroRemessa(e, 'Não foi possível validar a remessa. Revise os campos e tente novamente.');
      if (token === this.versaoEmpresa) this.remessaErro = mensagem;
    } finally { if (token === this.versaoEmpresa) this.remessaCarregando = false; }
  }
  async baixarRemessa(): Promise<void> {
    if (!this.remessaPodeBaixar()) { this.remessaErro = 'Valide novamente a configuração e os demonstrativos antes de baixar.'; return; }
    const token = this.versaoEmpresa;
    const fingerprint = this.fingerprintRemessa();
    const assinaturaRevisada = this.remessaValidacao!.assinatura;
    this.remessaBaixando = true; this.remessaErro = '';
    try {
      const atual = await firstValueFrom(this.api.validarRemessa(this.empresaApi, this.requisicaoRemessa()));
      if (token !== this.versaoEmpresa) return;
      if (this.carregando || this.salvando || this.erroCarregamento) { this.remessaErro = 'Os dados ainda estão sendo atualizados. Aguarde e tente novamente.'; return; }
      if (fingerprint !== this.fingerprintRemessa()) { this.invalidarRemessa(); this.remessaErro = 'A seleção ou configuração mudou. Valide novamente.'; return; }
      this.remessaValidacao = atual; this.remessaFingerprint = fingerprint;
      if (!atual.valido || atual.erros?.length || !atual.assinatura) { this.remessaErro = atual.erros?.join(' ') || 'A remessa precisa estar válida antes do download.'; return; }
      if (atual.assinatura !== assinaturaRevisada) { this.remessaFingerprint = ''; this.remessaErro = 'Os demonstrativos salvos mudaram desde a revisão. Confira o resumo e valide novamente antes de baixar.'; return; }
      const req = this.requisicaoRemessa(atual.assinatura);
      const response = await firstValueFrom(this.api.gerarRemessa(this.empresaApi, req));
      if (token !== this.versaoEmpresa) return;
      if (this.carregando || this.salvando || this.erroCarregamento) { this.remessaErro = 'Os dados ainda estão sendo atualizados. Aguarde e valide novamente.'; return; }
      if (fingerprint !== this.fingerprintRemessa()) { this.invalidarRemessa(); this.remessaErro = 'A seleção ou configuração mudou. Valide novamente.'; return; }
      const blob = response.body;
      if (!blob || !blob.size) { this.remessaErro = 'O arquivo gerado veio vazio. Valide a remessa novamente.'; return; }
      this.revogarUrlRemessa();
      this.urlRemessa = URL.createObjectURL(blob);
      const anchor = window.document.createElement('a');
      anchor.href = this.urlRemessa;
      const nome = this.nomeArquivoRemessa(response.headers.get('Content-Disposition')) || this.nomeArquivoFallback(atual.nomeArquivo);
      anchor.download = this.planoRemessa.arquivos.length > 1
        ? nome.replace(/\.rem$/i, '') + `-parte-${String(this.arquivoRemessaIndice + 1).padStart(3, '0')}-de-${String(this.planoRemessa.arquivos.length).padStart(3, '0')}.rem`
        : nome;
      anchor.style.display = 'none'; window.document.body.appendChild(anchor); anchor.click(); anchor.remove();
      this.arquivosRemessaBaixados[this.arquivoRemessaIndice] = fingerprint;
      const urlBaixado = this.urlRemessa;
      window.setTimeout(() => { if (this.urlRemessa === urlBaixado) this.revogarUrlRemessa(); }, 1500);
    } catch (e) {
      const mensagem = await this.mensagemErroRemessa(e, 'Não foi possível gerar o arquivo de remessa.');
      if (token === this.versaoEmpresa) this.remessaErro = mensagem;
    } finally { if (token === this.versaoEmpresa) this.remessaBaixando = false; }
  }
  private requisicaoRemessa(assinatura?: string): RequisicaoRemessaHolerite {
    const req: RequisicaoRemessaHolerite = { formato: this.formatoRemessa, configuracao: { ...this.remessaConfiguracao }, documentosIds: this.idsRemessa() };
    if (assinatura) req.assinatura = assinatura;
    return req;
  }
  private fingerprintRemessa(): string { return JSON.stringify([this.empresa, this.empresaApi, this.versaoDocumentos, this.formatoRemessa, this.remessaConfiguracao, this.tamanhoArquivoRemessa, this.arquivoRemessaIndice, this.idsRemessa()]); }
  private invalidarRemessa(): void { this.remessaValidacao = null; this.remessaFingerprint = ''; this.revogarUrlRemessa(); }
  private novaConfiguracaoRemessa(): Record<string, string> {
    const agora = new Date();
    const data = `${agora.getFullYear()}-${String(agora.getMonth()+1).padStart(2,'0')}-${String(agora.getDate()).padStart(2,'0')}`;
    return { empresaNome: '', empresaNomeReduzido: '', empresaCnpj: '', empresaConvenio: '', empresaBanco: '033', empresaAgencia: '', empresaConta: '', dataGeracao: data, horaGeracao: `${String(agora.getHours()).padStart(2,'0')}:${String(agora.getMinutes()).padStart(2,'0')}:${String(agora.getSeconds()).padStart(2,'0')}`, dataDisponibilizacao: '', competencia: '', dataPagamento: '', tipoPagamento: '', tipoArquivo: 'T', versaoArquivo: '', numeroLote: '', numeroRemessa: '', codigoServico: '001', convenioCabecalho200: '', modoTexto200: 'automatico' };
  }
  private nomeArquivoRemessa(contentDisposition: string | null): string {
    const filename = contentDisposition?.match(/filename\s*=\s*"?([^";]+)"?/i)?.[1]?.trim() || '';
    return filename && /^[\x20-\x7E]+$/.test(filename) && filename.toLowerCase().endsWith('.rem') ? filename : '';
  }
  private nomeArquivoFallback(nome: string): string {
    const limpo = (nome || 'remessa-santander.rem').replace(/[^\x20-\x7E]/g, '').replace(/[\\/:*?"<>|]/g, '-').trim();
    return limpo.toLowerCase().endsWith('.rem') ? limpo : `${limpo.replace(/\.[^.]*$/, '') || 'remessa-santander'}.rem`;
  }
  private revogarUrlRemessa(): void { if (this.urlRemessa) { URL.revokeObjectURL(this.urlRemessa); this.urlRemessa = null; } }
  private async mensagemErroRemessa(e: unknown, fallback: string): Promise<string> {
    const candidate = e as { error?: unknown } | null;
    if (candidate?.error instanceof Blob) {
      try { const body = JSON.parse(await candidate.error.text()) as { detail?: unknown; message?: unknown }; const detail = body.detail ?? body.message; if (typeof detail === 'string' && detail.trim()) return detail; } catch { /* corpo de erro não JSON */ }
    }
    return this.mensagemErro(e, fallback);
  }
  visiveis(): Holerite[] {
    const q = this.filtro.trim().toLocaleLowerCase('pt-BR');
    return q ? this.documentos.filter(h => [h.dados['funcionarioNome'], h.dados['funcionarioMatricula'], h.dados['competencia'], h.dados['numeroDocumento']].some(v => (v || '').toLocaleLowerCase('pt-BR').includes(q))) : this.documentos;
  }

  private novoPerfil(): PerfilImportacao { return { nome: '', formato: 'csv', caminhoRegistros: '', separador: ';', decimal: ',', mapeamento: {} }; }
  limparPrevia(): void { this.candidatos = []; this.errosImportacao = []; this.revisaoFeita = false; }
  private async atualizarDocumentos(token: number): Promise<void> { const docs = await firstValueFrom(this.api.listarDocumentos(this.empresaApi)); if (token === this.versaoEmpresa) { this.documentos = Array.isArray(docs) ? docs : []; this.versaoDocumentos++; this.reiniciarArquivosRemessa(); this.sincronizarNomesRemessa(); this.invalidarRemessa(); } }
  private mensagemErro(e: unknown, fallback: string): string {
    if (e && typeof e === 'object') {
      const error = e as { error?: { detail?: unknown; message?: unknown }; message?: unknown };
      const detail = error.error?.detail ?? error.error?.message;
      if (typeof detail === 'string' && detail.trim()) return detail;
      if (typeof error.message === 'string' && error.message.trim() && !error.message.startsWith('Http failure response')) return error.message;
    }
    return fallback;
  }
}
